import { cn } from '@foundation/utils/cn';

import {
  HeadlessPagination,
  HeadlessPaginationProps,
} from '@design-system/headless-ui';
import { ChevronLeft } from '@design-system/icons';

import { PRESS_SCALE } from '../internal/animations';

export type PaginationProps = Omit<
  HeadlessPaginationProps,
  'styles' | 'previousIcon' | 'nextIcon' | 'ellipsis'
>;

const BODY_MD_DEFAULT =
  'font-body font-normal text-400 leading-pmui-400 tracking-pmui-tighter';

// Arrows share the page-number's size/shape but swap in icon-button colors
// and drop the pill hover background - they are also the only buttons that
// are ever disabled.
const ARROW_OVERRIDES = cn(
  'size-5 text-pmui-fg-primary',
  'hover:bg-transparent',
  'disabled:text-pmui-interactive-fg-disabled',
);

export function Pagination(props: PaginationProps) {
  return (
    <HeadlessPagination
      {...props}
      previousIcon={<ChevronLeft size={20} />}
      nextIcon={
        <ChevronLeft
          size={20}
          className="rotate-180"
        />
      }
      // U+22EF centres the dots on the digits; U+2026 sits ~5px lower
      ellipsis="⋯"
      styles={{
        root: 'gap-2 [&>li:first-child]:mr-2 [&>li:last-child]:ml-2',
        item: cn(
          BODY_MD_DEFAULT,
          'size-8 rounded-full text-pmui-fg-secondary',
          PRESS_SCALE,
          'hover:bg-pmui-bg-secondary hover:text-pmui-fg-primary',
        ),
        arrow: ARROW_OVERRIDES,
        // hover colours repeated so the active label keeps its contrast
        activeItem: cn(
          'bg-pmui-bg-inverse text-pmui-fg-inverse font-emphasis',
          'hover:bg-pmui-bg-inverse hover:text-pmui-fg-inverse',
        ),
        ellipsis: cn(
          BODY_MD_DEFAULT,
          'flex size-8 shrink-0 items-center justify-center text-pmui-fg-secondary',
        ),
      }}
    />
  );
}
